import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ArrowLeft, Users, Lock } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import { getCelebRoomFans } from '../../api/celebRoom';
import UrlAvatar from './UrlAvatar';

const PAGE_SIZE = 30;

function formatDateTime(value) {
  if (!value) return '-';
  return String(value).replace('T', ' ').slice(0, 19);
}

export default function CelebRoomFans() {
  const { roomId } = useParams();
  const { accessToken, userId } = useDmPersona();

  const query = useInfiniteQuery({
    queryKey: ['celeb-room', 'fans', roomId, userId],
    queryFn: ({ pageParam = 0 }) =>
      getCelebRoomFans(roomId, { page: pageParam, size: PAGE_SIZE }, accessToken),
    getNextPageParam: (last) => {
      const loaded = (last.page + 1) * last.size;
      return loaded < last.total ? last.page + 1 : undefined;
    },
    initialPageParam: 0,
    retry: false,
  });

  const items = (query.data?.pages || []).flatMap((p) => p.items);
  const total = query.data?.pages?.[0]?.total ?? 0;
  const err = query.error;
  const isPrivate = err?.status === 403;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <Link
        to={`/celeb-room/r/${roomId}`}
        className="inline-flex items-center gap-2 text-sm text-dark-text-secondary hover:text-primary mb-6"
      >
        <ArrowLeft size={16} />
        {t('common.back')}
      </Link>

      <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2 mb-6">
        <Users size={20} className="text-primary" />
        {t('celebRoom.fans.title')}
        {!query.isLoading && !isPrivate && (
          <span className="text-sm font-normal text-dark-text-muted">({total})</span>
        )}
      </h1>

      {query.isLoading ? (
        <div className="text-center py-16 text-dark-text-secondary">{t('common.loading')}</div>
      ) : isPrivate ? (
        <div className="text-center py-12 px-6 rounded-2xl bg-dark-card border border-dark-border">
          <Lock size={32} className="mx-auto mb-3 text-dark-text-muted" />
          <p className="text-sm text-dark-text-secondary">{t('celebRoom.fans.private')}</p>
        </div>
      ) : query.isError ? (
        <div className="text-center py-12 px-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {err?.message || t('celebRoom.fans.error')}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 px-6 rounded-2xl bg-dark-card border border-dark-border">
          <Users size={32} className="mx-auto mb-3 text-dark-text-muted" />
          <p className="text-sm text-dark-text-secondary">{t('celebRoom.fans.empty')}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((fan) => (
            <li
              key={fan.fan_user_id}
              className="flex items-center gap-3 p-3 rounded-xl bg-dark-card border border-dark-border"
            >
              <UrlAvatar
                url={fan.profile_photo}
                name={fan.name || fan.username}
                size={40}
                className="w-10 h-10"
              />
              <div className="min-w-0 flex-grow">
                <p className="text-sm font-semibold text-white truncate">
                  {fan.name || fan.username || `#${fan.fan_user_id}`}
                  {fan.username && (
                    <span className="ml-1.5 text-xs font-normal text-dark-text-muted">
                      @{fan.username}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-dark-text-muted">
                  {t('celebRoom.fans.enteredAt', { time: formatDateTime(fan.entered_at) })}
                </p>
              </div>
              <span className="text-xs text-primary">#{fan.fan_user_id}</span>
            </li>
          ))}
        </ul>
      )}

      {query.hasNextPage && !isPrivate && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-secondary text-sm hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50"
          >
            {query.isFetchingNextPage ? t('common.loading') : t('celebRoom.fans.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}
